import styled, { x } from '@xstyled/styled-components';
import React, { FC, useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import { select } from '~/lib/select';
import { videoButtonAnimation } from '~/ui/animation';
import { Pause, Play } from '~/ui/buttons';
import { useMediaPlayerStore } from './MediaPlayerProvider';
import { MediaPlayerStore } from './MediaPlayerStore';
import { useCanControl } from './useCanControl';
import { useGlobalMedia } from './useGlobalMedia';
import { t } from 'ttag';
import { reportSpeedHackAndReload } from '~/lib/reloadDiagnostics';

const selector = (s: MediaPlayerStore) =>
  select(s, 'setPlayer', 'removePlayer', 'toggle', 'status');

const ButtonBlockWithAnimation = styled.div<{ toggle: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  animation: ${videoButtonAnimation} 0.5s ease infinite;
`;

interface Props {
  showButtonAnimation?: boolean | undefined;
}

export const VideoPlayer: FC<Props> = ({ showButtonAnimation }) => {
  const { setPlayer, removePlayer, toggle, status } =
    useMediaPlayerStore(selector);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [btnStatus, setBtnStatus] = useState(false);
  const [showBtn, setShowBtn] = useState(false);

  useGlobalMedia();

  useEffect(() => {
    const player = videojs(videoRef.current!, {
      html5: { nativeTextTracks: true },
      autoplay: false,
      controls: false,
      fill: true,
      controlBar: {
        children: [
          'PlayToggle',
          'VolumePanel',
          'PictureInPictureToggle',
          'FullscreenToggle'
        ]
      }
    });

    player.playsinline(true);

    setPlayer(player, 'video');

    return () => {
      removePlayer(player, 'video');
      player.dispose();
    };
  }, []);

  const canControl = useCanControl();

  const onClick = () => {
    setShowBtn(true);
    setTimeout(() => setShowBtn(false), 600);
    setBtnStatus(!btnStatus);
    toggle();
  };

  useEffect(() => {
    const intCheckPlaybackRate = setInterval(() => {
      const playbackRate = videoRef?.current?.playbackRate;

      if (playbackRate !== undefined && playbackRate > 2) {
        reportSpeedHackAndReload(playbackRate, 'video');
      }
    }, 3000);

    return () => clearInterval(intCheckPlaybackRate);
  }, []);

  return (
    <x.div data-vjs-player onClick={onClick}>
      <x.span
        position={'absolute'}
        visibility={'hidden'}
      >{t`There are no additional visuals to explain here`}</x.span>
      <video
        ref={videoRef}
        crossOrigin="anonymous"
        className="video-js"
        id={'videoJsBox'}
        preload="auto"
        onTouchEnd={onClick}
      />
      {canControl && showBtn && showButtonAnimation && (
        <ButtonBlockWithAnimation toggle={btnStatus}>
          {status == 'paused' && <Pause style={{ transform: 'scale(4)' }} />}
          {status == 'playing' && <Play style={{ transform: 'scale(4)' }} />}
        </ButtonBlockWithAnimation>
      )}
    </x.div>
  );
};
